import type { CanvasResource, Container } from 'pixi.js';
import { Transform, Rectangle, Sprite } from 'pixi.js';
import isEqual from 'lodash/isEqual';
import { BooleanAttribute, NumberAttribute, StringAttribute } from '../attributes';
import {
    CreationType,
    DynamicStylingCreationLevel,
    TextAlignment,
    ValueMapMode,
} from '../../enums';
import type { SeekMode } from '../../enums';
import type { AssetTemplateReferenceMap, Pixels } from '../../types';
import { assertInEnum, assertMinimum, assertNumber } from '../../validators';
import { objectHasTemplateValues } from '../attributes/template/common';
import { getPositionFromTransformation } from '../../video-data/designs/scenes';
import type { RawAssetData } from '../../video-data/types';
import { PlaybackRegions, type PlaybackRegion } from '../PlaybackRegions';
import type { FontLoader } from '../FontLoader';
import { getGlobalFontManager } from '../FontManager';
import type { CanvasRenderer, CanvasRendererSettings } from './TextAsset/renderer/BaseTextRenderer';
import type { TextAssetStyle } from './TextAsset/TextAssetStyle';
import { BaseAsset } from './BaseAsset';
import type { BaseAssetSettings, CopyPasteData } from './BaseAsset';
import { resetSprite } from './utils';

export abstract class BaseTextAsset<Renderer extends CanvasRenderer> extends BaseAsset {
    override _classType = 'CanvasCaptionAsset';

    @NumberAttribute()
    accessor textSize = 80;

    @StringAttribute({ choices: Object.values(TextAlignment) })
    accessor textAlign = TextAlignment.LEFT;

    /**
     * Multiplier used to calculate the final leading or line height (fontSize * (1 + leadingMultiplier)).
     */
    @NumberAttribute({ min: -2, max: 2 })
    accessor leadingMultiplier = 0.1;

    @NumberAttribute({ min: 0, max: Infinity })
    accessor maxHeight = Infinity;

    /** Display the active bounds for visual debugging. */
    @BooleanAttribute({ serialize: false })
    accessor showActiveBounds = false;

    /** Display the maximum dimensions for visual debugging. */
    @BooleanAttribute({ serialize: false })
    accessor showMaxBounds = false;

    @BooleanAttribute()
    accessor hasUserChangedMaxWidth = false;

    @BooleanAttribute()
    accessor hasUserChangedPosition = false;

    #maxWidth = Infinity;

    #fontManager = getGlobalFontManager();

    #renderer: Renderer = null;
    #sprite: Sprite = null;

    #playbackRegions: Readonly<Readonly<PlaybackRegion>[]> = [];

    /**
     * Returns containers for the given level. Required for DynamicAnimationConfiguration.
     * For text assets, only object-level is supported.
     */
    getCreationContainersAtLevelForType(
        level: DynamicStylingCreationLevel,
        type = CreationType.Animation,
    ): Container[] {
        assertInEnum(type, CreationType);
        if (level === DynamicStylingCreationLevel.Object) {
            return [this];
        }
        throw new Error(`Unsupported container of type ${level}`);
    }

    constructor(container, settings: BaseAssetSettings, renderer: Renderer) {
        super(container, settings);

        this.#renderer = renderer;
        this.#sprite = new Sprite();
        this._addInternalChild(this.#sprite);

        if (this.maxHeight === Infinity) {
            this.maxHeight = this.root.referenceResolution.height;
        }
        if (this.maxWidth === Infinity) {
            this.maxWidth = this.root.referenceResolution.width;
        }
    }

    get fontManager() {
        return this.#fontManager;
    }

    /**
     * Some functionalities of this base class need access to style information. One
     * example is template reference map. Child classes should return whatever styles they have.
     */
    abstract get _styles(): Record<string, TextAssetStyle>;

    get renderer(): Renderer {
        return this.#renderer;
    }

    /**
     * Used for captions and fancy captions, when there could be cuts in the
     * media asset or audio asset to which the captions correspond.
     * @internal
     */
    get playbackRegions(): Readonly<Readonly<PlaybackRegion>[]> {
        return this.#playbackRegions;
    }

    /**
     * @internal
     */
    set playbackRegions(value: Readonly<Readonly<PlaybackRegion>[]>) {
        if (isEqual(this.#playbackRegions, value)) {
            return;
        }
        this.#playbackRegions = value;
        this.requiresRedraw = true;
    }

    /**
     * Traditionally PIXI containers calculate the world bounds based on their children. We don't want to do this for
     * our text as otherwise it would include our sprite container which covers the full size of the video. Instead, we
     * only want to consider the bounds of the text itself. This is done by overriding the calculateBounds method and
     * calculating it manually.
     *
     * IMPORTANT: The current Canvas Text Renderer, does not provide information about animated bounds, so all
     * bounds for text assets are the 'static' or 'steady state' version of the bounds. This is different from PIXI
     * objects which will get the real bounds of the object at the current time.
     */
    override calculateBounds() {
        // override the default behavior, so we don't pick up the sprite bounds which cover the full video
        this._pixiContainer._bounds.clear();
        this._calculateBounds();
        this._pixiContainer._bounds.updateID = this._boundsID;
    }

    /**
     * PIXI native method for calculating bounds. We override this method to calculate the bounds of the text asset
     * which is being rendered by the canvas based renderer.
     */
    override _calculateBounds() {
        if (!this.renderer.canRender) {
            return;
        }
        const transform = new Transform();
        if (this.parent?.transform) {
            const x = this.parent.transform.worldTransform.tx;
            const y = this.parent.transform.worldTransform.ty;
            transform.worldTransform.translate(x, y);
        }

        // apply the local transformation
        const localBounds = this.#calculateLocalBounds();
        const width = localBounds.width;
        const height = localBounds.height;
        transform.worldTransform.translate(localBounds.x, localBounds.y);

        this._pixiContainer._bounds.addFrame(transform, 0, 0, width, height);
    }

    #calculateLocalBounds(): Rectangle {
        let x = this._pixiContainer.transform.localTransform.tx;
        let y = this._pixiContainer.transform.localTransform.ty;

        const { width: rwidth, height: rheight } = this.renderer.totalBounds;
        const width = rwidth / this.referenceToOutputMultiplier;
        const height = rheight / this.referenceToOutputMultiplier;

        // consider the anchor - note that the renderer first positions just the text and then draws additional
        // graphics, so we use the text bounds to calculate the anchor offset
        const { width: tWidth, height: tHeight } = this.renderer.activeTextBounds;
        const textWidth = tWidth / this.referenceToOutputMultiplier;
        const textHeight = tHeight / this.referenceToOutputMultiplier;
        const anchorXOffset = textWidth * this.transformation.anchor.x;
        const anchorYOffset = textHeight * this.transformation.anchor.y;
        x -= anchorXOffset;
        y -= anchorYOffset;

        const { xOffset, yOffset } = this._calculateGraphicsOffsetFromText();
        x -= xOffset;
        y -= yOffset;

        return new Rectangle(x, y, width, height);
    }

    /**
     * Calculate the offset between the text and the graphics. This is the offset from the origin (top left corner) of the
     * text to the origin (top left corner) of the graphics.
     */
    _calculateGraphicsOffsetFromText() {
        if (!this.renderer.activeTextBounds) {
            return { xOffset: 0, yOffset: 0 };
        }
        const xOffset =
            (this.renderer.activeTextBounds.x - this.renderer.totalBounds.x) /
            this.referenceToOutputMultiplier;
        const yOffset =
            (this.renderer.activeTextBounds.y - this.renderer.totalBounds.y) /
            this.referenceToOutputMultiplier;
        return { xOffset, yOffset };
    }

    override get boundsWithoutAnimation() {
        if (this.hasText) {
            return super.boundsWithoutAnimation;
        }
        // The bounds will be an empty rectangle centered at (0,0) if the text is empty.
        // But returning a rectangle that's centered according to our transformation will help
        // place NTE's empty state box in a more accurate place on screen.
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = transformation.position;
        return new Rectangle(x, y, 0, 0);
    }

    /**
     * Since the transformation information is spread between PIXI's container and the renderer, we need to manually
     * calculate the local bounds of the text asset. This is done by calculating the bounds of the renderer and then
     * converting it to the local space of the container.
     */
    override getLocalBounds = () => {
        this.updateTransform();

        if (!this.renderer.canRender) {
            return new Rectangle(
                this._pixiContainer.transform.localTransform.tx,
                this._pixiContainer.transform.localTransform.ty,
                0,
                0,
            );
        }
        return this.#calculateLocalBounds();
    };

    override applyTransformation(offsetX = 0, offsetY = 0, offsetRotation = 0) {
        if (this.isPositionControlledByParent) {
            return;
        }
        this._pixiContainer.x = this.transformation.position.x + offsetX;
        this._pixiContainer.y = this.transformation.position.y + offsetY;
        this._pixiContainer.rotation = this.transformation.rotation + offsetRotation;
        this.updateTransform();
    }

    /**
     * Traditional PIXI containers apply transformations to their children. This is not the case for our text canvas
     * assets. We need to apply the transformation to the renderer instead. We do this by negating any container
     * transformation so the PIXI container always sits in the top-left corner (0-0) and thus fills up the entire
     * scene. Once that is done we then move on to apply the transformation to the renderer. This makes it seem like
     * the text asset is being transformed as part of the PIXI hierarchy when in reality it's the renderer that is being
     * transformed.
     * */
    override updateTransform = () => {
        this._boundsID++;

        // get the global position of the container
        const x =
            this.parent.transform.worldTransform.tx +
            this._pixiContainer.transform.localTransform.tx;
        const y =
            this.parent.transform.worldTransform.ty +
            this._pixiContainer.transform.localTransform.ty;

        // update worldTransform so the container ends up in the top-left corner
        const worldTransform = new Transform();
        worldTransform.worldTransform.translate(-x, -y);
        this._pixiContainer.transform.updateTransform(worldTransform);

        if (!this.renderer.canRender) {
            return;
        }
        // now move the world transformation into the renderer
        this.renderer.translateX = (x * this.referenceToOutputMultiplier) as Pixels;
        this.renderer.translateY = (y * this.referenceToOutputMultiplier) as Pixels;
        this.renderer.anchorX = this.transformation.anchor.x;
        this.renderer.anchorY = this.transformation.anchor.y;
        this.renderer.scaleX = this.scale.x;
        this.renderer.scaleY = this.scale.y;

        // update transform also propagates the alpha value in PIXI, so we need to handle that here too
        this._pixiContainer.worldAlpha = this._pixiContainer.alpha * this.parent.worldAlpha;
        for (let i = 0, j = this.children.length; i < j; ++i) {
            const child = this.children[i];
            if (child.visible) {
                child.worldAlpha = child.alpha * this._pixiContainer.worldAlpha;
            }
        }
    };

    /**
     * Approximate the maximum bounds of the text asset. This only considers the max area of the
     * text and does not include any graphics, which may extend beyond the text.
     */
    override get approximateMaxBounds(): Rectangle {
        const transformation = this.getFreelyPositionedParent().transformation;
        const { x, y } = getPositionFromTransformation(
            transformation,
            this.maxWidth,
            this.maxHeight,
        );
        return new Rectangle(x, y, this.maxWidth, this.maxHeight);
    }

    /**
     * Returns true if the renderer has something to render. This is used to determine if we should render or not. The
     * renderer is dependent on text metrics and style metrics. If either of these are empty, then nothing can be rendered.
     */
    get canRender(): boolean {
        return this.renderer.canRender;
    }

    abstract get hasText(): boolean;

    get maxWidth(): number {
        return this.#maxWidth;
    }

    set maxWidth(value: number) {
        assertNumber(value);
        assertMinimum(value, 0);
        if (this.maxWidth !== value) {
            this.#maxWidth = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Adjusts the text asset's max width to ensure it stays within the resolution boundaries.
     */
    adjustMaxWidthToFitResolution(offset: number = 0): void {
        const { x: currentMaxTextX, width: currentMaxTextWidth } = this.approximateMaxBounds;
        const resolutionWidth = this.root.referenceResolution.width;

        offset += this._calculateGraphicsOffsetFromText().xOffset;

        if (currentMaxTextX < 0) {
            // If text starts before viewport, adjust maxWidth to fit from x=0
            this.maxWidth = Math.floor(currentMaxTextWidth + currentMaxTextX - offset);
            return;
        }
        if (currentMaxTextX + currentMaxTextWidth > resolutionWidth) {
            // If text extends beyond right edge, adjust to fit within resolution width
            this.maxWidth = Math.floor(resolutionWidth - currentMaxTextX - offset);
        }
    }

    protected override _build(): void {
        if (this.visible === false || !this.hasText) {
            this.#renderer.resetBuild();
            this._emitAssetCanPlay();
            return;
        }

        void this.ensureUsedFontsAreAdded();
        if (!this.isDoneTryingToLoad) {
            this.sentCanPlay = false;
            return;
        }

        const state = this.getRendererSettings();
        this.#renderer.apply(state);
        this.#renderer.build();
        // we render our text at the output resolution so we need to scale the texture up to the reference resolution here

        // apply transformation
        this.applyTransformation();

        this._emitAssetCanPlay();
    }

    override get isDoneTryingToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        if (this.hasAllNecessaryFonts) {
            return true;
        }
        return false;
    }

    override get shouldLoad(): boolean {
        if (!this.visible) {
            return false;
        }
        if (typeof this.fetchResources !== 'boolean') {
            return this.fetchResources && this.fetchResources.fonts;
        }
        return super.shouldLoad;
    }

    override hasTemplateReferenceValues() {
        return Object.values(this._styles).some((style) => objectHasTemplateValues(style));
    }

    /**
     *  Ensures all the fonts used by this asset are added to the font manager.
     *  Returns a list of all used fonts.
     */
    ensureUsedFontsAreAdded(): FontLoader[] {
        return Object.values(this._styles).map((style) => this.fontManager.addFont(style.fontUrl));
    }

    /**
     * Resolves once all the fonts used by this asset are loaded.
     */
    async onUsedFontsLoaded(): Promise<void[]> {
        return Promise.all(
            Object.values(this._styles).map(async (style) => {
                await this.fontManager.getFontBySourceOnceLoaded(style.fontUrl);
            }),
        );
    }

    get hasAllNecessaryFonts(): boolean {
        return this.ensureUsedFontsAreAdded().every((font) => font.isLoaded);
    }

    someFontsDidNotLoadYet(): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => !font.isLoaded);
    }

    someFontsFailedToLoad(): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => font.failedToLoad);
    }

    usedFontWasAdded(sourceUrl: string): boolean {
        return this.ensureUsedFontsAreAdded().some((font) => font.sourceUrl === sourceUrl);
    }

    override get failedToLoad(): boolean {
        if (this.skipBuildAndDraw) {
            return false;
        }
        if (!this.hasText) {
            return false;
        }
        if (this.someFontsDidNotLoadYet()) {
            return true;
        }
        return this.someFontsFailedToLoad();
    }

    override get isLoaded(): boolean {
        if (this.skipBuildAndDraw) {
            return true;
        }
        if (!this.hasText) {
            return true;
        }
        return this.hasAllNecessaryFonts;
    }

    _redrawOnFontLoad() {
        // We could optimize this so the text doesn't rebuild if this asset isn't using the font.
        // However, we need to consider both the style fonts and the fallback fonts to do this. With the
        // canvas api it's not easy to know which font is used, so for now we always rebuild when a font loads.

        // A new font has loaded, we need to rebuild the canvas, so it can access the new font.

        this.requiresRedraw = true;
    }

    /**
     * Create a bridge for all asset properties into canvas compatible properties. This includes transcript, style and
     * dynamic styling properties. The output is an object that can be passed to the canvas renderer build method.
     * */
    protected getRendererSettings(): CanvasRendererSettings {
        return {
            maxTextWidth: (this.maxWidth * this.referenceToOutputMultiplier) as Pixels,
            maxTextHeight: (this.maxHeight * this.referenceToOutputMultiplier) as Pixels,
            showActiveBounds: this.showActiveBounds,
            showMaxBounds: this.showMaxBounds,
        };
    }

    /**
     * Selectivly applies style properties to the text asset. This includes the transformation, max width, max height, text size, and alignment.
     * @internal
     */
    _loadStylePropertiesForSwap(data: RawAssetData) {
        if (data?.transformation) {
            this.transformation.load(data.transformation);
        }
        if (data?.maxWidth) {
            this.maxWidth = data.maxWidth;
        }
        if (data?.maxHeight) {
            this.maxHeight = data.maxHeight;
        }
        if (data?.textSize) {
            this.textSize = data.textSize;
        }
        if (data?.textAlign) {
            this.textAlign = data.textAlign;
        }
    }

    /**
     * Override applyStyleProperties to clear dynamic styling before loading new style
     */
    override pasteStyleProperties(settings: CopyPasteData) {
        Object.values(this._styles).map((style) => (style as TextAssetStyle).removeDynamicStyles());
        super.pasteStyleProperties(settings);
    }

    /**
     * Builds a map of all values referencing template value in object and its styles.
     */
    override buildTemplateReferenceMap(): AssetTemplateReferenceMap {
        let result = super.buildTemplateReferenceMap();
        Object.entries(this._styles).forEach(([styleName, style]) => {
            result = {
                ...style._buildValueMap(ValueMapMode.GET_REFERENCE_VALUES, styleName),
                ...result,
            };
        });
        return result;
    }

    /**
     * Builds a map of color values of all color graphics in the object (including all color graphics of all styles in
     * the object). When no graphics are found, an empty object is returned.
     */
    buildColorGraphicsMap(): Record<string, number> {
        let result = {};
        const colorMap = {};

        Object.entries(this._styles).forEach(([styleName, style]) => {
            const map = style._buildValueMap(
                ValueMapMode.GET_ALL_VALUES_AS_PRIMITIVE,
                styleName,
                true,
            );
            result = {
                ...result,
                ...map,
            };
        });

        Object.entries(result).map(([valueKey, value]) => {
            if (
                (valueKey.toLowerCase().includes('fill') ||
                    valueKey.toLowerCase().includes('color')) &&
                !valueKey.toLowerCase().includes('alpha') &&
                valueKey.toLowerCase().includes('graphics')
            ) {
                colorMap[valueKey] = value;
            }
        });

        return colorMap;
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (this.visible === false || !this.hasText) {
            return;
        }
        this.#renderer.seekMode = seekMode;
        const duration = PlaybackRegions.getDurationFromPlaybackRegions(this.#playbackRegions);
        const playbackTime = progress * duration;
        this.#renderer.currentTime = PlaybackRegions.playbackTimeToAssetTime(
            this.#playbackRegions,
            Infinity, // progress cannot be past the end of the last playback region
            playbackTime,
        );
    }

    updateForRender() {
        if (this.visible === false) {
            return;
        }

        this.#sprite.texture = this.root._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });
        this.#renderer.canvasResolutionMultiplier =
            this.root._reusableTextures.canvasResolutionMultiplier;

        const canvas = (this.#sprite.texture.baseTexture.resource as CanvasResource)
            .source as HTMLCanvasElement;
        this.#renderer.setCanvas(canvas);
        // NOTE: even though updateTransform is called as part of the PIXI render loop, we need to manually call it here
        // beforehand so our renderer can render the text at the correct position before it gets drawn by PIXI. That's
        // because updateTransform decomposes the transformation and transfers it to the canvas renderer.
        this.updateTransform();
        this.#renderer.draw();

        this.#sprite.texture.baseTexture.resource.update();
    }

    override destroy() {
        super.destroy(false);
        this.#renderer.destroy();
        this.#sprite.destroy(false);
    }

    override get propertiesToSerialize() {
        // WARNING: we can't use getAttributesToSerialize on base classes because it will return an empty array for all
        // child classes. This is limitation of current attribute system. That's why we need to manually add them here.
        return super.propertiesToSerialize.concat([
            'maxWidth',
            'maxHeight',
            'textSize',
            'textAlign',
            'leadingMultiplier',
            'hasUserChangedMaxWidth',
            'hasUserChangedPosition',
        ]);
    }

    override get editableProperties() {
        return super.editableProperties.concat(['textSize']);
    }
}
